import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { UserProfile } from '../types/user';
import { logger } from '../utils/logger';

// Shape que a API devolve (camelCase) — ver server/src/types.ts (PublicUser)
export interface ApiUser {
  id: string;
  email: string;
  fullName: string;
  role: 'user' | 'consultant' | 'admin';
  credits: number;
  birthDate?: string | null;
  birthTime?: string | null;
  birthPlace?: string | null;
}

export interface RegisterExtras {
  fullName?: string;
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
}

// Mapeia o user da API para o UserProfile legado que os componentes consomem
const mapApiUserToProfile = (apiUser: ApiUser): UserProfile => ({
  id: apiUser.id,
  email: apiUser.email,
  name: apiUser.fullName,
  credits: apiUser.credits,
  is_admin: apiUser.role === 'admin',
  role: apiUser.role,
  birth_date: apiUser.birthDate ?? '',
  birth_time: apiUser.birthTime ?? undefined,
  birth_place: apiUser.birthPlace ?? undefined,
  created_at: '',
  updated_at: '',
});

interface AuthContextType {
  user: UserProfile | null;
  isLoading: boolean;
  error: Error | null;
  register: (email: string, password: string, extras?: RegisterExtras) => Promise<UserProfile>;
  login: (email: string, password: string) => Promise<UserProfile>;
  logout: () => Promise<void>;
  setCredits: (credits: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // 401 = sem sessão → user null, NÃO é erro
        const { user: apiUser } = await api.get<{ user: ApiUser }>('/auth/me');
        setUser(mapApiUserToProfile(apiUser));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setUser(null);
        } else {
          logger.error('Auth initialization error:', err);
          setError(err instanceof Error ? err : new Error('Auth error'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const register = useCallback(
    async (email: string, password: string, extras?: RegisterExtras) => {
      const { user: apiUser } = await api.post<{ user: ApiUser }>('/auth/register', {
        email,
        password,
        ...extras,
      });
      const profile = mapApiUserToProfile(apiUser);
      setUser(profile);
      return profile;
    },
    [],
  );

  const login = useCallback(async (email: string, password: string) => {
    const { user: apiUser } = await api.post<{ user: ApiUser }>('/auth/login', { email, password });
    const profile = mapApiUserToProfile(apiUser);
    setUser(profile);
    return profile;
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
  }, []);

  const setCredits = useCallback((credits: number) => {
    setUser(prev => (prev ? { ...prev, credits } : prev));
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, error, register, login, logout, setCredits }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};